<script setup lang="ts">
import { GutachtenAnsicht, InternetFreigabeRecht, ProbenAnsicht } from '@/api/model';
import { useLayout } from '@/layout/composables/layout';
import { ROUTE_NAMES } from '@/router/routeNames';
import { useBadgeStore } from '@/stores/badgeStore';
import { useKundenbereichStore } from '@/stores/kundenbereichStore';
import { i18n, t } from '@/utils/i18n';
import { PrimeIcons } from '@primevue/core/api';
import { MenuItem } from 'primevue/menuitem';
import { onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import AppMenuItem from './AppMenuItem.vue';

const { layoutState, toggleMenuCollapse } = useLayout();
const route = useRoute();
const badgeStore = useBadgeStore();
const kundenbereichStore = useKundenbereichStore();
const model = ref<any[]>();
const bavInstitutDeUrl = 'http://www.bav-institut.de/';

// Badges initial laden
onMounted(async () => {
    await badgeStore.badgesLaden(route.params.kunde as string);

    model.value = getModel();
});

function getModel() {
    const model: any[] = [
        {
            label: '[Menu]ProbenBeauftragen',
            items: [
                {
                    label: '[Menu]ProbenErfassen',
                    icon: PrimeIcons.TABLE,
                    to: { name: ROUTE_NAMES.PROBEN.LISTE, params: { kunde: route.params.kunde } },
                    badge: badgeStore.badges.proben
                },
                {
                    label: '[Menu]ProbenErfassenExcelImport',
                    icon: PrimeIcons.FILE_EXCEL,
                    to: { name: ROUTE_NAMES.PROBEN.EXCEL_IMPORT, params: { kunde: route.params.kunde } }
                },
                {
                    label: '[Menu]EtikettenDrucken',
                    icon: PrimeIcons.QRCODE,
                    to: { name: ROUTE_NAMES.ETIKETTEN.LISTE, params: { kunde: route.params.kunde } }
                },
                {
                    label: '[Menu]ProbenbegleitscheinDrucken',
                    icon: PrimeIcons.PRINT,
                    to: { name: ROUTE_NAMES.PROBENBEGLEITSCHEIN.LISTE, params: { kunde: route.params.kunde } }
                },
                {
                    label: '[Menu]VorlagenErfassen',
                    icon: PrimeIcons.FILE,
                    to: { name: ROUTE_NAMES.MUSTERVORLAGEN.LISTE, params: { kunde: route.params.kunde } },
                    badge: badgeStore.badges.mustervorlagen
                }
            ]
        },
        {
            label: '[Menu]ProbenOhneErgebnisse',
            items: [
                {
                    label: '[Menu]ProbenInZustellung',
                    icon: PrimeIcons.USER,
                    to: { name: ROUTE_NAMES.ERGEBNISSE.ANGEMELDETE, params: { kunde: route.params.kunde } },
                    badge: badgeStore.badges.ergebnisse![ProbenAnsicht.Angemeldete]
                },
                {
                    label: '[Menu]ProbenInBearbeitung',
                    icon: PrimeIcons.HOURGLASS,
                    to: { name: ROUTE_NAMES.ERGEBNISSE.IM_LABOR, params: { kunde: route.params.kunde } },
                    badge: badgeStore.badges.ergebnisse![ProbenAnsicht.ImLabor]
                }
            ]
        },
        {
            label: '[Menu]ProbenMitErgebnisse',
            items: [
                {
                    label: '[Menu]VorlaufigeErgebnisse',
                    icon: PrimeIcons.CLOCK,
                    to: { name: ROUTE_NAMES.ERGEBNISSE.VORLAEUFIGE, params: { kunde: route.params.kunde } },
                    badge: badgeStore.badges.ergebnisse![ProbenAnsicht.VorlaufigeErgebnisse]
                },
                {
                    label: '[Menu]FreigegebeneErgebnisse',
                    icon: PrimeIcons.CHECK_SQUARE,
                    to: { name: ROUTE_NAMES.ERGEBNISSE.ENDGUELTIGE, params: { kunde: route.params.kunde } },
                    badge: badgeStore.badges.ergebnisse![ProbenAnsicht.EndgueltigeErgebnisse]
                },
                {
                    label: '[Menu]Statistik',
                    icon: PrimeIcons.CHART_PIE,
                    to: { name: ROUTE_NAMES.ERGEBNISSE.STATISTIK, params: { kunde: route.params.kunde } }
                }
            ]
        }
    ];

    if (badgeStore.badges.sindBegehungsberichteVorhanden) {
        model.push({
            label: '[Menu]BegehungsberichteUeberschrift',
            items: [
                {
                    label: '[Menu]Begehungsberichte',
                    icon: PrimeIcons.CHECK_CIRCLE,
                    to: { name: ROUTE_NAMES.BEGEHUNGSBERICHTE.LISTE, params: { kunde: route.params.kunde } }
                }
            ]
        });
    }

    var gutachtenEintraege = getGutachtenMenuEintraege();
    if (gutachtenEintraege) {
        model.push(gutachtenEintraege);
    }

    if (badgeStore.badges.sindZertifikateVorhanden) {
        model.push({
            label: '[Menu]Zertifikate',
            items: [
                {
                    label: '[Menu]Qualitaetskontrolle',
                    icon: PrimeIcons.LIST,
                    to: { name: ROUTE_NAMES.ZERTIFIKATE.QUALITAETSKONTROLLE, params: { kunde: route.params.kunde } }
                }
            ]
        });
    }

    return model;
}

// Reaktive Aktualisierung der Badges
watch(
    () => badgeStore.badges,
    () => {
        model.value = getModel();
    },
    { deep: true }
);

function getImpressumUrl(): string {
    if (i18n.global.locale.value === 'de') {
        return bavInstitutDeUrl + 'de/impressum';
    }

    return bavInstitutDeUrl + 'en/imprint';
}

function getKontaktUrl(): string {
    if (i18n.global.locale.value === 'de') {
        return bavInstitutDeUrl + 'de/kontakt';
    }

    return bavInstitutDeUrl + 'en/contact';
}

function getAgbUrl(): string {
    if (i18n.global.locale.value === 'de') {
        return bavInstitutDeUrl + 'de/ueber_uns/agb';
    }

    return bavInstitutDeUrl + 'en/about_us/terms_and_conditions';
}

function getGutachtenMenuEintraege(): MenuItem | null {
    const items: MenuItem[] = [];

    if (!kundenbereichStore.einstellungen.rechte[InternetFreigabeRecht.GutachtenVersionenErgebnisse]) {
        return null;
    }

    const gutachtenBadges = badgeStore.badges['gutachten']!;

    // Erfassen immer anzeigen, Ergebnisse nur wenn vorhanden
    items.push({
        label: '[Menu]GutachtenBeauftragen',
        icon: PrimeIcons.PENCIL,
        to: { name: ROUTE_NAMES.GUTACHTEN.ERFASSEN, params: { kunde: route.params.kunde } },
        badge: gutachtenBadges[GutachtenAnsicht.Erfassen]
    });

    if (badgeStore.badges.sindGutachtenVorhanden) {
        items.push({
            label: '[Menu]GutachtenInBearbeitung',
            icon: PrimeIcons.HOURGLASS,
            to: { name: ROUTE_NAMES.GUTACHTEN.IN_BEARBEITUNG, params: { kunde: route.params.kunde } },
            badge: gutachtenBadges[GutachtenAnsicht.InBearbeitung]
        });
        items.push({
            label: '[Menu]GutachtenBerichte',
            icon: PrimeIcons.FILE,
            to: { name: ROUTE_NAMES.GUTACHTEN.ERGEBNISSE, params: { kunde: route.params.kunde } },
            badge: gutachtenBadges?.[GutachtenAnsicht.Ergebnisse]
        });
    }

    if (badgeStore.badges.sindRohstoffspezifikationenVorhanden) {
        items.push({
            label: '[Menu]Rohstoffspezifikationen',
            icon: PrimeIcons.LIST,
            to: { name: ROUTE_NAMES.GUTACHTEN.ROHSTOFFSPEZIFIKATIONEN, params: { kunde: route.params.kunde } }
        });
    }

    if (items.length === 0) {
        return null;
    }

    return { label: '[Menu]Gutachten', items };
}
</script>

<template v-if="model.value">
    <div class="layout-menu-container">
        <div class="menu-scroll-container">
            <ul class="layout-menu" :class="{ 'menu-collapsed': layoutState.menuCollapsed }">
                <template v-for="(item, i) in model" :key="item">
                    <app-menu-item v-if="!item.separator" :item="item" :index="i"></app-menu-item>
                    <li v-if="item.separator" class="menu-separator"></li>
                </template>
            </ul>
        </div>
        <div class="menu-collapse-footer border-solid border-t">
            <div class="grid grid-cols-[1fr_auto] items-center">
                <div v-if="!layoutState.menuCollapsed" class="flex flex-col gap-1">
                    <div>
                        <a :href="getImpressumUrl()" target="_blank">{{ t('[Menu]Impressum') }}</a> |
                        <a :href="getKontaktUrl()" target="_blank">{{ t('[Menu]Kontakt') }}</a>
                    </div>
                    <div>
                        <a :href="getAgbUrl()" target="_blank">{{ t('[Menu]Agb') }}</a> |
                        <router-link :to="{ name: ROUTE_NAMES.KUNDENBEREICH.NUTZUNGSBEDINGUNGEN }">{{ t('[Menu]Nutzungsbedingungen') }}</router-link>
                    </div>
                </div>

                <Button v-if="layoutState.menuCollapsed" @click="toggleMenuCollapse" icon="pi pi-angle-right" class="w-20 menue-reduzieren" v-tooltip="t('[Menu]Erweitern')" />
                <Button v-if="!layoutState.menuCollapsed" @click="toggleMenuCollapse" icon="pi pi-angle-left" class="w-20 menue-reduzieren" v-tooltip="t('[Menu]Reduzieren')" />
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.layout-menu-container {
    position: relative;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.menu-scroll-container {
    flex-grow: 1;
    overflow-y: auto;
}

:deep(.menu-collapsed) {
    padding-right: 0;
}

.layout-menu {
    padding-bottom: 1rem; /* Abstand zum Footer */
}
</style>
